<script>
export default {
	name: 'DestinationsListing',
	inheritAttrs: false,
};
</script>

<script setup>
import DestinationsListingCard from '~/components/DestinationsListing/Card.vue';
import CrossSellBanner from '~/components/Banner/CrossSellBanner.vue';
import DestinationsListingSearch from '~/components/DestinationsListing/Search.vue';
import Dropdown from '~/components/form/Dropdown.vue';
import AppHyperlink from '../components/AppHyperlink.vue';
import CTAButton from '~/aem-components/CTAButton.vue';
import VueSwiper from '~/components/VueSwiper.vue';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { useRouter, useRoute } from 'vue-router';
import { useRouteQuery } from '@vueuse/router';
import { useIsRTL } from '~/logic/helpers/is-rtl.js';

const router = useRouter();
const route = useRoute();

const props = defineProps({
	generalTab: { type: Object, default: null },
	dropdownTab: { type: Object, default: null },
	popularDestinationsTab: { type: Object, default: null },
	pageListing: { type: Array, default: null },
	componentIdAA: { type: String, default: '' },
});


const regionTag = ref(useRouteQuery('region').value ? useRouteQuery('region').value : 'all');
const searchKeyword = ref(useRouteQuery('search').value ? useRouteQuery('search').value : '');

const showListRange = ref(6);

const regions = computed(() => {
	return [
		{
			'label': props.dropdownTab.allLabel,
			'value': 'all',
		},
		...props.dropdownTab.regionTags.map((region) => ({
			'label': region.title,
			'value': region.value,
		})),
	];
});

const _applyFilterToData = (data, regionFilter, searchQuery) => {
	if (regionFilter == 'all' && !searchQuery) return data;
	
	return data.filter((item) => {
		switch (regionFilter) {
			case 'all': {
				return item.title.toLowerCase().includes(searchQuery.toLowerCase());
			}
			default: {
				if (!item.pageProperties?.destinationRegionTag) return false;
				if (item.pageProperties?.destinationRegionTag.length === 0) return false;

				return item.pageProperties?.destinationRegionTag.some((tag) => {
					if (tag.value !== regionFilter) return false;
					if (!searchQuery) return true;
					if (item.title.toLowerCase().includes(searchQuery.toLowerCase())) return true;
					return false;
				});
			}
		}
	});
};

const _applyParamsToFilter = () => {
	switch (regionTag.value) {
		case 'all': {
			if (!searchKeyword.value) {
				router.replace({ query: { ...route.query, region: undefined, search: undefined } });
			} else {
				router.replace({ query: { ...route.query, region: undefined, search: searchKeyword.value } });
			}
			break;
		}
		default: {
			if (!searchKeyword.value) {
				router.replace({ query: { ...route.query, region: regionTag.value, search: undefined } });
			} else {
				router.replace({ query: { ...route.query, region: regionTag.value, search: searchKeyword.value } });
			}
			break;
		}
	}
};

const searchResultDisplayed = computed(() => {
	let result = [];
	result = _applyFilterToData(props.pageListing, regionTag.value, searchKeyword.value);
	return result.filter((item) => !item.isFeatured).sort(function (a, b) {
		if (a.title < b.title) { return -1; }
		if (a.title > b.title) { return 1; }
		return 0;
	});
});

const searchResultDisplayedTruncated = computed(() => {
	return searchResultDisplayed.value.slice(0, showListRange.value);
});

const handleSelection = (value) => {
	regionTag.value = value;
	showListRange.value = 6;
	_applyParamsToFilter();
};
const handleSearch = (value) => {
	searchKeyword.value = value;
	_applyParamsToFilter();
};

const featuredDestination = computed(() => {
	return _applyFilterToData(props.pageListing, regionTag.value, searchKeyword.value).filter((item) => item.isFeatured);
});

const handleShowMore = () => {
	if (showListRange.value > searchResultDisplayed.value.length) return;
	showListRange.value = showListRange.value + 6;
};

const { isEditorMode } = useGlobalAEMState();
const { isRTL } = useIsRTL();

</script>



<template>
<div v-if="props.pageListing.length !== 0" class="DestinationsListing">
	<div class="grid grid-cols-3 gap-6 md:block generic-container">
		<Dropdown
			v-if="props.dropdownTab.regionTags.length !== 0"
			:options="regions"
			:labelText="props.dropdownTab.dropdownLabel"
			:rootAttrs="{
				class: 'mt-0 md:(w-full mb-4 mr-0)',
			}"
			:aria-label="props.dropdownTab.dropdownLabel"
			:defaultValue="regionTag"
			@change="handleSelection"
		></Dropdown>
		<DestinationsListingSearch
			v-if="props.pageListing.length !== 0"
			:searchInputPlaceholderText="props.dropdownTab.searchBar"
			:searchButtonLabelText="props.dropdownTab.searchCtaLabel"
			:searchList="props.pageListing"
			:regionFilter="regionTag"
			:defaultValue="searchKeyword"
			:componentIdAA="props.componentIdAA"
			class="col-span-2"
			@update:search-query="handleSearch"
		/>
	</div>
	<div
		v-if="props.pageListing.length > 0
			|| featuredDestination.length > 0"
	>
		<div
			v-if="featuredDestination.length > 0"
			class="mt-7.5"
		>
			<CrossSellBanner
				mobileBannerHeight="small"
				desktopBannerHeight="large"
				:bgStyle="props.generalTab.featuredDestinationSection.backgroundStyle"
				:keyVisualSrc="props.generalTab.featuredDestinationSection.imageUrl"
				:keyVisualAlignment="props.generalTab.featuredDestinationSection.imagePosition"
			>
				<div class="md:pb-8">
					<h3 class="title >md:line-clamp-3">{{ featuredDestination[0].title }}</h3>
					<p
						v-html-sanitize="props.generalTab.featuredDestinationSection.description"
						class="author-html-container line-clamp-4"
					></p>
					<AppHyperlink
						v-if="props.generalTab.labelSection.ctaLabel"
						v-aa="[
							{
								clickName: featuredDestination[0].title,
								clickComponentType: 'URL',
								componentName: 'DestinationsListing',
								componentID: props.componentIdAA,
							},
							{
								name: props.generalTab.labelSection.ctaLabel,
								type: 'other',
							},
						]"
						class="cta-btn mt-6"
						:href="props.generalTab.featuredDestinationSection.path"
					>
						<span>{{ props.generalTab.labelSection.ctaLabel }}</span>
						<icon-far-arrow-right
							class="cta-btn-arrow text-base inline-block fill-white ml-3 transform rtl:-rotate-180 rtl:ml-0 rtl:mr-3"
						/>
					</AppHyperlink>
				</div>
			</CrossSellBanner>
		</div>
		<div class="mt-8 md:mt-6 grid grid-cols-3 gap-x-6 gap-y-7.5 md:grid-cols-1 generic-container">
			<DestinationsListingCard
				v-for="(item, i) in searchResultDisplayedTruncated"
				:key="i"
				:title="item.title"
				:path="item.path"
				:imageUrl="item.pageProperties?.destinationTileImage"
				:imageAltText="item.pageProperties?.destinationTileImageAltText"
				:locations="item.locations"
				:locationLabel="props.generalTab.labelSection.label"
				:locationLabels="props.generalTab.labelSection.labels"
				:destinationTileIconText="item.pageProperties?.destinationTileIconText"
				:destinationTileDescription="item.pageProperties?.destinationTileDescription"
				:iconType="props.generalTab.labelSection.iconType"
				:iconId="props.generalTab.labelSection.iconId"
				:iconImage="props.generalTab.labelSection.iconImage"
				:ctaLabel="props.generalTab.labelSection.ctaLabel"
				:componentIdAA="props.componentIdAA"
			/>
		</div>
		<div class="mt-10 md:mt-8">
			<CTAButton
				v-if="showListRange < searchResultDisplayed.length"
				:onClick="handleShowMore"
				:ctaText="props.generalTab.labelSection.showMoreLabel"
				sizeType="small"
				ctaLinkType="button"
			/>
		</div>
	</div>
	<div
		v-if="(searchResultDisplayed.length == 0
			&& featuredDestination.length == 0) || isEditorMode"
		class="generic-container"
	>
		<div
			v-html-sanitize="props.popularDestinationsTab.noResultMessage"
			class="author-html-container text-center py-16 mb-20"
		></div>
		<h3
			v-if="props.popularDestinationsTab.popularDestinationTitle"
			class="text-center mb-10"
		>
			{{ props.popularDestinationsTab.popularDestinationTitle }}
		</h3>
		<VueSwiper
			v-if="props.popularDestinationsTab.popularDestinations.length > 0 && !isEditorMode"
			slidesPerView="1"
			:spaceBetween="24"
			:breakpoints="{
				'767.1': {
					slidesPerView: 3,
				},
			}"
		>
			<DestinationsListingCard
				v-for="(item, i) in props.popularDestinationsTab.popularDestinations"
				:key="i"
				:title="item.title"
				:path="item.path"
				:imageUrl="item.pageProperties?.destinationTileImage"
				:imageAltText="item.pageProperties?.destinationTileImageAltText"
				:locations="item.locations"
				:locationLabel="props.generalTab.labelSection.label"
				:locationLabels="props.generalTab.labelSection.labels"
				:destinationTileIconText="item.pageProperties?.destinationTileIconText"
				:destinationTileDescription="item.pageProperties?.destinationTileDescription"
				:iconType="props.generalTab.labelSection.iconType"
				:iconId="props.generalTab.labelSection.iconId"
				:iconImage="props.generalTab.labelSection.iconImage"
				:ctaLabel="props.generalTab.labelSection.ctaLabel"
				:componentIdAA="props.componentIdAA"
			/>
		</VueSwiper>
		<div
			v-if="isEditorMode && props.popularDestinationsTab.popularDestinations.length > 0"
			class="mt-8 md:mt-6 grid grid-cols-3 gap-x-6 gap-y-7.5 md:grid-cols-1"
		>
			<DestinationsListingCard
				v-for="(item, i) in props.popularDestinationsTab.popularDestinations"
				:key="i"
				:title="item.title"
				:path="item.path"
				:imageUrl="item.pageProperties?.destinationTileImage"
				:imageAltText="item.pageProperties?.destinationTileImageAltText"
				:locations="item.locations"
				:locationLabel="props.generalTab.labelSection.label"
				:locationLabels="props.generalTab.labelSection.labels"
				:destinationTileIconText="item.pageProperties?.destinationTileIconText"
				:destinationTileDescription="item.pageProperties?.destinationTileDescription"
				:iconType="props.generalTab.labelSection.iconType"
				:iconId="props.generalTab.labelSection.iconId"
				:iconImage="props.generalTab.labelSection.iconImage"
				:ctaLabel="props.generalTab.labelSection.ctaLabel"
			/>
		</div>
	</div>
</div>

</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.DestinationsListing {
	
}
.title {
	font-size: 2rem;
	line-height: 2.5rem;
	margin-bottom: 1rem
}
.cta-btn {
	color: var(--neutral-white-base);
	transition: .25s ease-out;

	&:hover {
		text-decoration: none;
		color: var(--primary-blue-extralight);

		.cta-btn-arrow {
			fill: var(--primary-blue-extralight);
			transform: translateX(4px);

			[dir="rtl"] & {
				transform: translateX(-4px) rotate(-180deg);
			}
		}
	}
}
.cta-btn-arrow {
	transition: .25s ease-out;
}
</style>
